
import React from 'react';
import { CircleType, CircleItem } from '../types';
import { Info, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ThreeCirclesProps {
  items: CircleItem[];
}

export const ThreeCircles: React.FC<ThreeCirclesProps> = ({ items }) => {
  const getBorderColor = (type: CircleType) => {
    switch (type) {
      case CircleType.INNER: return 'border-red-500';
      case CircleType.MIDDLE: return 'border-amber-400';
      case CircleType.OUTER: return 'border-green-500';
    }
  };

  const getTitleColor = (type: CircleType) => {
    switch (type) {
      case CircleType.INNER: return 'text-red-700 dark:text-red-400';
      case CircleType.MIDDLE: return 'text-amber-700 dark:text-amber-400';
      case CircleType.OUTER: return 'text-green-700 dark:text-green-400';
    }
  };

  const getBgColor = (type: CircleType) => {
    switch (type) {
        case CircleType.INNER: return 'bg-red-50 dark:bg-red-900/10';
        case CircleType.MIDDLE: return 'bg-amber-50 dark:bg-amber-900/10';
        case CircleType.OUTER: return 'bg-green-50 dark:bg-green-900/10';
    }
  }

  const renderColumn = (type: CircleType, title: string) => {
    const columnItems = items.filter(i => i.type === type);
    return (
        <div className={`flex-1 min-w-0 rounded-xl border-t-4 ${getBorderColor(type)} ${getBgColor(type)} p-4`}>
            <h4 className={`font-bold uppercase tracking-wider text-xs mb-3 ${getTitleColor(type)}`}>
                {title}
            </h4>
            <ul className="space-y-2">
                {columnItems.map(item => (
                    <li key={item.id} className="text-sm text-slate-700 dark:text-slate-300 bg-white/50 dark:bg-black/20 p-2 rounded break-words">
                        {item.text}
                    </li>
                ))}
                {columnItems.length === 0 && (
                    <li className="text-xs text-slate-400 italic">No behaviors listed.</li>
                )}
            </ul>
        </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
            <h3 className="text-xl font-bold dark:text-white">Three Circles Plan</h3>
            <p className="text-sm text-slate-500">Visualizing your boundaries.</p>
        </div>
        <Link 
            to="/profile" 
            state={{ activeTab: 'circles' }}
            className="text-xs text-brand-600 dark:text-brand-400 hover:underline flex items-center gap-1 self-start"
        >
          <Info className="w-3 h-3" /> Edit in Profile
        </Link>
      </div>
      
      {/* Empty State */}
      {items.length === 0 ? (
          <div className="text-center py-8 px-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-700">
              <div className="mx-auto w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-4">
                  <HelpCircle className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-slate-800 dark:text-white mb-2">Define Your Boundaries</h4>
              <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                  The Three Circles help you define what sobriety means to you.
              </p>
              
              <div className="grid gap-4 text-left max-w-sm mx-auto mb-6">
                  <div className="flex gap-3">
                      <div className="w-2 h-full bg-red-500 rounded-full flex-shrink-0"></div>
                      <div>
                          <p className="text-xs font-bold text-red-600 uppercase">Inner Circle</p>
                          <p className="text-xs text-slate-500">Behaviors you want to stop completely (e.g. drinking, using, isolation).</p>
                      </div>
                  </div>
                  <div className="flex gap-3">
                      <div className="w-2 h-full bg-amber-400 rounded-full flex-shrink-0"></div>
                      <div>
                          <p className="text-xs font-bold text-amber-600 uppercase">Middle Circle</p>
                          <p className="text-xs text-slate-500">"Slippery slope" behaviors that lead you toward the Inner Circle.</p>
                      </div>
                  </div>
                  <div className="flex gap-3">
                      <div className="w-2 h-full bg-green-500 rounded-full flex-shrink-0"></div>
                      <div>
                          <p className="text-xs font-bold text-green-600 uppercase">Outer Circle</p>
                          <p className="text-xs text-slate-500">Healthy behaviors that keep you connected and sober.</p>
                      </div>
                  </div>
              </div>

              <Link 
                to="/profile" 
                state={{ activeTab: 'circles' }}
                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-bold text-sm transition-colors"
              >
                  Setup My Circles
              </Link>
          </div>
      ) : (
          /* Stack vertically on all screens to create horizontal bars (Green -> Yellow -> Red) */
          <div className="flex flex-col gap-4">
              {renderColumn(CircleType.OUTER, "Outer (Recovery)")}
              {renderColumn(CircleType.MIDDLE, "Middle (Slippery)")}
              {renderColumn(CircleType.INNER, "Inner (Danger)")}
          </div>
      )}
    </div>
  );
};
